import { useState, useEffect, useRef, useCallback } from 'react';

export const ZOOM_MIN = 1;
export const ZOOM_MAX = 4;
export const ZOOM_STEP = 0.1;
export const DOUBLE_CLICK_ZOOM = 2;

export const clampZoom = (value) => {
    const rounded = Math.round(value * 100) / 100;
    return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, rounded));
};

/**
 * Zoom con rueda, doble clic y pan sobre un viewport.
 * @param {Object} options
 * @param {React.RefObject} options.imageRef
 * @param {React.RefObject} options.viewportRef
 * @param {React.RefObject} options.transformLayerRef
 * @param {string|number} options.resetKey - cambia al cambiar foto
 * @param {{ width: number, height: number }|null} options.baseSize - tamaño base explícito (preview)
 * @param {string} options.fitMaxHeight - max-height CSS en zoom 1 sin baseSize
 * @param {boolean} options.enabled
 */
const useImageZoom = ({
    imageRef,
    viewportRef,
    transformLayerRef,
    resetKey,
    baseSize = null,
    fitMaxHeight = '70vh',
    enabled = true,
}) => {
    const [zoom, setZoom] = useState(ZOOM_MIN);
    const [isPanning, setIsPanning] = useState(false);

    const baseSizeRef = useRef(null);
    const panStartRef = useRef({ x: 0, y: 0, scrollLeft: 0, scrollTop: 0 });
    const scrollAdjustRef = useRef(null);

    const resetViewportScroll = useCallback(() => {
        const viewport = viewportRef?.current;
        if (viewport) {
            viewport.scrollLeft = 0;
            viewport.scrollTop = 0;
        }
    }, [viewportRef]);

    const measureBaseSizeFromImage = useCallback(() => {
        const img = imageRef?.current;
        if (!img || img.offsetWidth === 0 || img.offsetHeight === 0) return;

        baseSizeRef.current = {
            width: img.offsetWidth,
            height: img.offsetHeight,
        };
    }, [imageRef]);

    const applyLayout = useCallback(() => {
        const img = imageRef?.current;
        const layer = transformLayerRef?.current;
        const viewport = viewportRef?.current;
        if (!img || !enabled) return;

        if (baseSize?.width && baseSize?.height) {
            baseSizeRef.current = { width: baseSize.width, height: baseSize.height };
        }

        if (zoom <= ZOOM_MIN || !baseSizeRef.current) {
            if (baseSize?.width && baseSize?.height) {
                img.style.width = '100%';
                img.style.height = '100%';
                img.style.maxWidth = 'none';
                img.style.maxHeight = 'none';
                if (layer) {
                    layer.style.width = '100%';
                    layer.style.height = '100%';
                }
            } else {
                img.style.width = '100%';
                img.style.height = 'auto';
                img.style.maxWidth = '100%';
                img.style.maxHeight = fitMaxHeight;
                if (layer) {
                    layer.style.width = '100%';
                    layer.style.height = 'auto';
                }
                requestAnimationFrame(() => measureBaseSizeFromImage());
            }
            resetViewportScroll();
        } else {
            const { width: baseW, height: baseH } = baseSizeRef.current;
            const scaledW = baseW * zoom;
            const scaledH = baseH * zoom;

            img.style.width = `${scaledW}px`;
            img.style.height = `${scaledH}px`;
            img.style.maxWidth = 'none';
            img.style.maxHeight = 'none';

            if (layer) {
                layer.style.width = `${scaledW}px`;
                layer.style.height = `${scaledH}px`;
            }
        }

        if (viewport) {
            viewport.classList.toggle('image-zoom-viewport--zoomed', zoom > ZOOM_MIN);
        }
    }, [
        zoom,
        enabled,
        imageRef,
        transformLayerRef,
        viewportRef,
        baseSize,
        fitMaxHeight,
        resetViewportScroll,
        measureBaseSizeFromImage,
    ]);

    const applyScrollAdjust = useCallback(() => {
        const viewport = viewportRef?.current;
        const adjust = scrollAdjustRef.current;
        if (!viewport || !adjust || !baseSizeRef.current) return;

        const { ratioX, ratioY, pointerX, pointerY } = adjust;
        const { width: baseW, height: baseH } = baseSizeRef.current;
        const newLayerW = baseW * zoom;
        const newLayerH = baseH * zoom;
        const newPointerX = ratioX * newLayerW;
        const newPointerY = ratioY * newLayerH;

        viewport.scrollLeft += newPointerX - pointerX;
        viewport.scrollTop += newPointerY - pointerY;
        scrollAdjustRef.current = null;
    }, [viewportRef, zoom]);

    const setZoomLevel = useCallback((nextZoom, scrollAdjust = null) => {
        const clamped = clampZoom(nextZoom);
        scrollAdjustRef.current = scrollAdjust;
        setZoom((prev) => (prev === clamped ? prev : clamped));
    }, []);

    const resetZoom = useCallback(() => {
        setZoom(ZOOM_MIN);
        baseSizeRef.current = null;
        resetViewportScroll();
    }, [resetViewportScroll]);

    useEffect(() => {
        resetZoom();
    }, [resetKey, resetZoom]);

    useEffect(() => {
        applyLayout();
        applyScrollAdjust();
    }, [zoom, baseSize, applyLayout, applyScrollAdjust]);

    useEffect(() => {
        const img = imageRef?.current;
        if (!img || !enabled) return undefined;

        const handleLoad = () => {
            if (zoom <= ZOOM_MIN && !baseSize) {
                measureBaseSizeFromImage();
            }
            applyLayout();
        };

        if (img.complete) {
            handleLoad();
        }

        img.addEventListener('load', handleLoad);
        return () => img.removeEventListener('load', handleLoad);
    }, [resetKey, imageRef, enabled, zoom, baseSize, measureBaseSizeFromImage, applyLayout]);

    const handleWheel = useCallback((e) => {
        if (!enabled) return;
        const viewport = viewportRef?.current;
        const layer = transformLayerRef?.current;
        if (!viewport || !layer) return;

        e.preventDefault();
        e.stopPropagation();

        if (!baseSizeRef.current) {
            if (baseSize?.width) {
                baseSizeRef.current = { width: baseSize.width, height: baseSize.height };
            } else {
                measureBaseSizeFromImage();
            }
        }
        if (!baseSizeRef.current) return;

        const direction = e.deltaY > 0 ? -1 : 1;
        const nextZoom = clampZoom(zoom + direction * ZOOM_STEP);
        if (nextZoom === zoom) return;

        const layerRect = layer.getBoundingClientRect();
        const pointerX = e.clientX - layerRect.left;
        const pointerY = e.clientY - layerRect.top;
        const ratioX = layerRect.width > 0 ? pointerX / layerRect.width : 0.5;
        const ratioY = layerRect.height > 0 ? pointerY / layerRect.height : 0.5;

        setZoomLevel(nextZoom, { ratioX, ratioY, pointerX, pointerY });
    }, [enabled, zoom, viewportRef, transformLayerRef, baseSize, measureBaseSizeFromImage, setZoomLevel]);

    const handleDoubleClick = useCallback((e) => {
        if (!enabled) return;
        e.preventDefault();
        e.stopPropagation();
        const nextZoom = zoom === ZOOM_MIN ? DOUBLE_CLICK_ZOOM : ZOOM_MIN;
        setZoomLevel(nextZoom);
    }, [enabled, zoom, setZoomLevel]);

    const handleMouseDown = useCallback((e) => {
        if (!enabled || zoom <= ZOOM_MIN) return;
        const viewport = viewportRef?.current;
        if (!viewport) return;

        setIsPanning(true);
        viewport.classList.add('image-zoom-viewport--panning');
        panStartRef.current = {
            x: e.clientX,
            y: e.clientY,
            scrollLeft: viewport.scrollLeft,
            scrollTop: viewport.scrollTop,
        };
        e.preventDefault();
    }, [enabled, zoom, viewportRef]);

    const handleMouseMove = useCallback((e) => {
        if (!isPanning) return;
        const viewport = viewportRef?.current;
        if (!viewport) return;

        const { x, y, scrollLeft, scrollTop } = panStartRef.current;
        viewport.scrollLeft = scrollLeft - (e.clientX - x);
        viewport.scrollTop = scrollTop - (e.clientY - y);
        e.preventDefault();
    }, [isPanning, viewportRef]);

    const handleMouseUp = useCallback(() => {
        setIsPanning(false);
        viewportRef?.current?.classList.remove('image-zoom-viewport--panning');
    }, [viewportRef]);

    useEffect(() => {
        const viewport = viewportRef?.current;
        if (!viewport || !enabled) return undefined;

        viewport.addEventListener('wheel', handleWheel, { passive: false });
        viewport.addEventListener('mousedown', handleMouseDown);
        viewport.addEventListener('dblclick', handleDoubleClick);
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);

        return () => {
            viewport.removeEventListener('wheel', handleWheel);
            viewport.removeEventListener('mousedown', handleMouseDown);
            viewport.removeEventListener('dblclick', handleDoubleClick);
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [
        enabled,
        viewportRef,
        handleWheel,
        handleMouseDown,
        handleDoubleClick,
        handleMouseMove,
        handleMouseUp,
    ]);

    return {
        zoom,
        zoomPercent: Math.round(zoom * 100),
        setZoomLevel,
        resetZoom,
    };
};

export default useImageZoom;
